"use client";

import { c as _c } from "react/compiler-runtime";
import { useT } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { cachedFetch } from "@/lib/fetch-cache";
import EntityHistory from "./EntityHistory";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface UpdateEntry {
  version: string | null;
  type: string | null;
  date: string | null;
  changes: string[];
}

// Beta patches land green, main-branch releases gold, pre-release gray —
// same hue language as the channel pill and the changelog page.
function patchKind(type: string | null): "beta" | "main" | "pre" {
  if (!type || type === "Pre-release") return "pre";
  if (type.startsWith("Beta")) return "beta";
  return "main";
}
const kindText: Record<string, string> = {
  beta: "text-success",
  main: "text-[var(--accent-gold)]",
  pre: "text-[var(--text-muted)]"
};
const kindDot: Record<string, string> = {
  beta: "bg-success-fill",
  main: "bg-[var(--accent-gold)]",
  pre: "bg-line-strong"
};

/**
 * The "Version history" section on entity pages: the entity's own game-patch
 * changes (data/entity_history.json), replacing the site-changelog diffs
 * that used to masquerade as its history. Entities with nothing recorded
 * fall back to the changelog timeline.
 */
export default function EntityUpdateHistory(t0) {
  const $ = _c(17);
  const {
    entityType,
    entityId
  } = t0;
  const t = useT();
  const [entries, setEntries] = useState(null);
  let t1;
  let t2;
  if ($[0] !== entityId || $[1] !== entityType) {
    t1 = () => {
      cachedFetch(`${API}/api/update-history/${entityType}/${entityId}`).then(d => setEntries(d.length > 0 ? d : "none")).catch(() => setEntries("none"));
    };
    t2 = [entityType, entityId];
    $[0] = entityId;
    $[1] = entityType;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (entries === "none") {
    let t3;
    if ($[4] !== entityId || $[5] !== entityType) {
      t3 = <EntityHistory entityType={entityType} entityId={entityId} />;
      $[4] = entityId;
      $[5] = entityType;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t3;
  if ($[7] !== t) {
    t3 = t("Version history");
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <h2>{t3}</h2>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== entries || $[12] !== t) {
    t5 = entries === null ? <p className="text-xs text-[var(--text-muted)] m-0">{t("Loading\u2026")}</p> : <div className="relative ml-2"><div className="absolute left-[5px] top-2 bottom-2 w-px bg-[var(--border-subtle)]" /><div className="space-y-4">{entries.map((entry, i) => {
          const kind = patchKind(entry.type);
          return <div key={`${entry.version}-${i}`} className="relative pl-6"><div className={`absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full border-2 border-[var(--bg-primary)] ${kindDot[kind]}`} /><div className="flex items-center gap-2 text-xs"><span className="font-semibold text-[var(--text-primary)]">{entry.version ?? t("Unknown")}</span>{entry.type && <span className={kindText[kind]}>{entry.type}</span>}{entry.date && <span className="text-[var(--text-muted)]">{entry.date}</span>}</div><ul className="mt-1.5 space-y-1 list-none m-0 p-0">{entry.changes.map(_temp)}</ul></div>;
        })}</div></div>;
    $[11] = entries;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <section id="history">{t4}{t5}</section>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp(change, j) {
  return <li key={j} className="text-xs text-[var(--text-muted)]">{change}</li>;
}